import type { IntlShape } from 'react-intl';

import type { Validators } from '../../types/field-validation';
import { type FormField, FormFieldType } from '../../types/form/form-fields';
import type { FieldValue } from '../../types/form/form-fields/field-values';
import {
	asAdfValue,
	asDateTimeValue,
	asDateValue,
	asFilesValue,
	asLocalTimeValue,
	asNumberValue,
	asOptionsValue,
	asTextValue,
} from '../../types/form/form-fields/field-values/utils';
import { asFormOptionField } from '../../types/form/form-fields/utils';
import { getRichTextValidationErrors } from './adf';
import { getAttachmentValidationErrors } from './attachment';
import { getDateValidationErrors } from './date';
import { getDateTimeValidationErrors } from './date-time';
import { getEmailFormatErrors, getEmailValidationErrors } from './email';
import { getNumberValidationErrors } from './number';
import { getOptionValidationErrors } from './option';
import { getTextValidationErrors } from './text';
import { getTimeValidationErrors } from './time';
import { getUrlFormatErrors, getUrlValidationErrors } from './url';

export const buildDefaultFormFieldValidators = (intl?: IntlShape): Validators => ({
	[FormFieldType.ATTACHMENT]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) => getAttachmentValidationErrors(field.validation, field.label, intl, asFilesValue(value)),
	[FormFieldType.CASCADING_SELECT]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) =>
		getOptionValidationErrors(
			asFormOptionField(field)?.options ?? [],
			field.validation,
			field.label,
			intl,
			asOptionsValue(value),
		),
	[FormFieldType.SINGLE_SELECT]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) =>
		getOptionValidationErrors(
			asFormOptionField(field)?.options ?? [],
			field.validation,
			field.label,
			intl,
			asOptionsValue(value),
		),
	[FormFieldType.MULTIPLE_SELECT]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) =>
		getOptionValidationErrors(
			asFormOptionField(field)?.options ?? [],
			field.validation,
			field.label,
			intl,
			asOptionsValue(value),
		),
	[FormFieldType.CHECKBOX]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) =>
		getOptionValidationErrors(
			asFormOptionField(field)?.options ?? [],
			field.validation,
			field.label,
			intl,
			asOptionsValue(value),
		),
	[FormFieldType.RADIO]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) =>
		getOptionValidationErrors(
			asFormOptionField(field)?.options ?? [],
			field.validation,
			field.label,
			intl,
			asOptionsValue(value),
		),
	[FormFieldType.DATE]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) => getDateValidationErrors(field.validation, field.label, intl, asDateValue(value)?.date),
	[FormFieldType.DATE_TIME]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) => getDateTimeValidationErrors(field.validation, field.label, intl, asDateTimeValue(value)),
	[FormFieldType.LOCAL_TIME]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) =>
		getTimeValidationErrors(
			field.validation,
			field.label,
			intl,
			asLocalTimeValue(value)?.localTime,
		),
	[FormFieldType.RICH_TEXT_PARAGRAPH]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) => getRichTextValidationErrors(field.validation, field.label, intl, asAdfValue(value)),
	[FormFieldType.NUMBER]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) => getNumberValidationErrors(field.validation, field.label, intl, asNumberValue(value)),
	[FormFieldType.SHORT_TEXT]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) => getTextValidationErrors(field.validation, field.label, intl, asTextValue(value)),
	[FormFieldType.LONG_TEXT]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) => getTextValidationErrors(field.validation, field.label, intl, asTextValue(value)),
	[FormFieldType.URL]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) => getUrlValidationErrors(field.validation, field.label, intl, asTextValue(value)),
	[FormFieldType.EMAIL]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) => getEmailValidationErrors(field.validation, field.label, intl, asTextValue(value)),
	[FormFieldType.PARAGRAPH]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) => getTextValidationErrors(field.validation, field.label, intl, asTextValue(value)),
	[FormFieldType.RATING]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) => getNumberValidationErrors(field.validation, field.label, intl, asNumberValue(value)),
	[FormFieldType.SCALE]: (
		field: FormField,
		defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) => getNumberValidationErrors(field.validation, field.label, intl, asNumberValue(value)),
});

export const buildSecurityCriticalValidators = (intl?: IntlShape): Validators => ({
	[FormFieldType.EMAIL]: (
		_field: FormField,
		_defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) => getEmailFormatErrors(intl, asTextValue(value)),
	[FormFieldType.URL]: (
		_field: FormField,
		_defaultFormFieldValidators: Validators,
		value?: FieldValue,
	) => getUrlFormatErrors(intl, asTextValue(value)),
});
